import axios from "axios";
import { config } from "../../config/Config";

// The caller's own portfolios (/v1/web/portfolios). A portfolio is private to the user who made it,
// within one org — the backend takes the owner from the session, never from the request. Auth and
// org headers are attached by the shared axios interceptor (config/axiosConfig.js).

const PORTFOLIOS = `${config.serverBaseUrl}/v1/web/portfolios`;

// [UserPortfolioDTO] — { id, name, description, colour, gameIds (only games the caller can open),
// hiddenGameCount, createdAt, updatedAt }. Unordered.
export const listPortfolios = async (orgId) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${PORTFOLIOS}/orgId/${encodeURIComponent(orgId)}`,
        });
        return response.data?.portfolios || [];
    } catch (error) {
        console.error('Error fetching portfolios:', error.response?.data || error.message);
        throw error;
    }
};

// Creates one of the caller's portfolios in the org. A blank or already-used name is a 400 whose
// `detailedMessage` says which.
export const createPortfolio = async (orgId, { name, description, colour }) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${PORTFOLIOS}/orgId/${encodeURIComponent(orgId)}`,
            headers: { 'Content-Type': 'application/json' },
            data: { name, description, colour },
        });
        return response.data;
    } catch (error) {
        console.error('Error creating portfolio:', error.response?.data || error.message);
        throw error;
    }
};

// Renames / redescribes / recolours one of the caller's portfolios. Same name rules as create.
export const updatePortfolio = async (portfolioId, { name, description, colour }) => {
    try {
        const response = await axios({
            method: 'put',
            url: `${PORTFOLIOS}/${encodeURIComponent(portfolioId)}`,
            headers: { 'Content-Type': 'application/json' },
            data: { name, description, colour },
        });
        return response.data;
    } catch (error) {
        console.error('Error updating portfolio:', error.response?.data || error.message);
        throw error;
    }
};

// Deletes the portfolio itself; its games are untouched.
export const deletePortfolio = async (portfolioId) => {
    try {
        const response = await axios({
            method: 'delete',
            url: `${PORTFOLIOS}/${encodeURIComponent(portfolioId)}`,
        });
        return response.data;
    } catch (error) {
        console.error('Error deleting portfolio:', error.response?.data || error.message);
        throw error;
    }
};

// Adds games (ids the caller can open — any other id fails the whole request); returns the portfolio.
export const addGamesToPortfolio = async (portfolioId, gameIds) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${PORTFOLIOS}/${encodeURIComponent(portfolioId)}/games`,
            headers: { 'Content-Type': 'application/json' },
            data: { gameIds },
        });
        return response.data;
    } catch (error) {
        console.error('Error adding games to portfolio:', error.response?.data || error.message);
        throw error;
    }
};

// Takes one game out of the portfolio; returns the portfolio.
export const removeGameFromPortfolio = async (portfolioId, gameId) => {
    try {
        const response = await axios({
            method: 'delete',
            url: `${PORTFOLIOS}/${encodeURIComponent(portfolioId)}/games/${encodeURIComponent(gameId)}`,
        });
        return response.data;
    } catch (error) {
        console.error('Error removing game from portfolio:', error.response?.data || error.message);
        throw error;
    }
};
